import { useEffect, useState } from "react";
import {
  listConversationSummaries,
  getMemorySettings,
  updateMemorySettings,
  type MemorySettings,
} from "@/api/memory";
import { YOU, getFocus } from "@/api/memory-focus";
import { getSubscriptionStatus } from "@/api/billing";
import {
  Badge,
  Button,
  PocketRow,
  PocketSection,
  SelectField,
  ToggleSwitch,
} from "@/components/ui";
import {
  getModelsForRuntime,
  isRuntimeKey,
  usesOpencodePlanGroups,
  type RuntimeKey,
} from "@/lib/model-registry";
import { translate, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsMemoryMessages } from "@/i18n/messages/settings-memory";
import { useCompanionStore } from "@/stores/companion-store";
import { KNOWLEDGE_GRAPH_UPGRADE, showUpgradeToast } from "@/lib/upgrade-toast";
import {
  captureDesktopChatAuthScopeGuard,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import { useSettingsNav } from "./settings-navigation";
import { SettingsFieldError } from "./settings-text-field";
import { watchBillingRefresh } from "./billing-refresh";
import { toModelSelectOptions } from "./model-select-options";

const showGraphUpgrade = () =>
  showUpgradeToast({
    id: "denker.knowledge-graph-upgrade",
    ...KNOWLEDGE_GRAPH_UPGRADE,
  });
const RUNNERS: { value: RuntimeKey; label: string }[] = [
  { value: "claude_cli", label: "Claude Code" },
  { value: "codex_cli", label: "Codex" },
  { value: "opencode_cli", label: "OpenCode" },
];
export function MemorySettingsRows({
  onOpenGraph,
}: {
  onOpenGraph: () => void;
}) {
  const t = useT(settingsMemoryMessages);
  const tc = useT(commonMessages);
  const nav = useSettingsNav();
  const [settings, setSettings] = useState<MemorySettings | null>(null);
  const [pro, setPro] = useState<boolean | null>(null);
  const [thingCount, setThingCount] = useState<number | null>(null);
  const [summaryCount, setSummaryCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const available = useCompanionStore((s) => s.availableRuntimes);
  useEffect(() => {
    let active = true;
    let generation = 0;
    const refresh = () => {
      const request = ++generation;
      const scope = captureDesktopChatAuthScopeGuard();
      setSettings(null);
      setPro(null);
      setBusy(false);
      setError(null);
      void Promise.all([getMemorySettings(), getSubscriptionStatus()])
        .then(([config, sub]) => {
          if (!active || request !== generation || !scope()) return;
          setSettings(config);
          setPro(sub.has_pro_access);
        })
        .catch(() => {
          if (active && request === generation && scope())
            setError(translate(settingsMemoryMessages, "settingsLoadError"));
        });
    };
    refresh();
    const unsubscribe = subscribeDesktopChatAuthScope(refresh);
    const stopBillingRefresh = watchBillingRefresh(refresh);
    return () => {
      active = false;
      unsubscribe();
      stopBillingRefresh();
    };
  }, [attempt]);
  useEffect(() => {
    if (pro !== true) {
      setThingCount(null);
      setSummaryCount(null);
      return;
    }
    let active = true;
    let generation = 0;
    const refresh = () => {
      const request = ++generation;
      const scope = captureDesktopChatAuthScopeGuard();
      const current = () => active && request === generation && scope();
      void getFocus(YOU).then(
        (view) => {
          if (current()) setThingCount(view.memory_size);
        },
        () => {
          if (current()) setThingCount(null);
        },
      );
      void listConversationSummaries({ days: 7, limit: 10 }).then(
        (summaries) => {
          if (current()) setSummaryCount(summaries.length);
        },
        () => {
          if (current()) setSummaryCount(null);
        },
      );
    };
    refresh();
    window.addEventListener("denker-memory-review-changed", refresh);
    const unsubscribe = subscribeDesktopChatAuthScope(() => {
      setThingCount(null);
      setSummaryCount(null);
      refresh();
    });
    return () => {
      active = false;
      unsubscribe();
      window.removeEventListener("denker-memory-review-changed", refresh);
    };
  }, [attempt, pro]);
  const runner =
    settings?.memory_runner && isRuntimeKey(settings.memory_runner)
      ? settings.memory_runner
      : useCompanionStore.getState().defaultRuntime;
  const options = [
    { value: "auto", label: t("modelAutomatic") },
    ...RUNNERS.flatMap((tool) => {
      const grouped = usesOpencodePlanGroups(tool.value, available);
      return [
        {
          value: `${tool.value}:`,
          label: t("modelAutomaticCheapest"),
          group: tool.label,
        },
        ...toModelSelectOptions(
          getModelsForRuntime(tool.value, undefined, undefined, grouped),
          grouped,
        ).map((option) => ({
          ...option,
          value: `${tool.value}:${option.value}`,
          group: option.group ? `${tool.label} · ${option.group}` : tool.label,
        })),
      ];
    }),
  ];
  const selected = settings?.memory_model
    ? `${runner}:${settings.memory_model}`
    : settings?.memory_runner
      ? `${runner}:`
      : "auto";
  if (
    selected !== "auto" &&
    !options.some((option) => option.value === selected)
  )
    options.push({
      value: selected,
      label: settings?.memory_model ?? "",
      group: t("configuredModel"),
    });
  async function save(patch: Partial<MemorySettings>) {
    if (busy || !settings) return;
    const scope = captureDesktopChatAuthScopeGuard();
    setBusy(true);
    setError(null);
    try {
      const updated = await updateMemorySettings(patch);
      if (scope()) setSettings(updated);
    } catch {
      if (scope()) setError(t("settingsSaveError"));
    } finally {
      if (scope()) setBusy(false);
    }
  }
  return (
    <PocketSection testId="memory-settings">
      <SettingsFieldError error={error} testId="memory-settings-error" />
      {!settings && error ? (
        <PocketRow
          title={tc("retry")}
          testId="memory-settings-retry"
          onSelect={() => setAttempt((n) => n + 1)}
        />
      ) : null}
      <PocketRow
        title={t("knowledgeGraph")}
        secondLine={t("knowledgeGraphLine")}
        testId="memory-graph-row"
        trailing={
          pro === false ? (
            <span className="flex items-center gap-2">
              <Badge variant="outline" dot={false}>
                {/* i18n-ignore: plan name */}
                Pro
              </Badge>
              <Button
                size="sm"
                variant="secondary"
                data-testid="memory-graph-upgrade"
                onClick={() => nav.push({ page: "account" })}
              >
                {t("upgrade")}
              </Button>
            </span>
          ) : (
            <ToggleSwitch
              checked={settings?.memory_graph_enabled ?? false}
              disabled={!settings || pro === null || busy}
              onCheckedChange={() =>
                void save({
                  memory_graph_enabled: !settings?.memory_graph_enabled,
                })
              }
              data-testid="memory-graph-toggle"
            />
          )
        }
      />
      <PocketRow
        title={t("openGraph")}
        secondLine={
          thingCount !== null ? t("things", { count: thingCount }) : undefined
        }
        testId="memory-open-in-canvas"
        chevron
        onSelect={() => (pro === false ? showGraphUpgrade() : onOpenGraph())}
      />
      <PocketRow
        title={t("model")}
        testId="memory-model-row"
        trailing={
          <SelectField
            variant="value"
            options={options}
            value={selected}
            valueLabel={
              selected === "auto"
                ? t("modelAutomatic")
                : t("modelValue", {
                    runner:
                      RUNNERS.find((tool) => tool.value === runner)?.label ??
                      runner,
                    model: settings?.memory_model
                      ? (options.find((option) => option.value === selected)
                          ?.label ?? settings.memory_model)
                      : t("modelAutomaticLower"),
                  })
            }
            disabled={!settings || busy}
            testId="memory-model-select"
            ariaLabel={t("memoryModel")}
            onChange={(value) => {
              if (value === "auto") {
                void save({ memory_runner: "", memory_model: "" });
                return;
              }
              const delimiter = value.indexOf(":");
              void save({
                memory_runner: value.slice(0, delimiter),
                memory_model: value.slice(delimiter + 1),
              });
            }}
          />
        }
      />
      <PocketRow
        title={t("conversationSummaries")}
        trailing={
          summaryCount === null
            ? undefined
            : t("summariesRecent", { count: summaryCount })
        }
        secondLine={t("summariesLine")}
        testId="memory-summaries"
        chevron
        onSelect={() => nav.push({ page: "memory-summaries" })}
      />
    </PocketSection>
  );
}
